import { useEffect, useState } from 'react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Textarea } from '../../../shared/components/Textarea';
import { mensagemDeErro } from '../api';
import type { CollaboratorDocument, ReviewAction } from '../api/documentos';
import { useAnalisarDocumento } from '../hooks/cadastro';
import { REJECTION_REASON_OPTIONS } from './format';
import { PessoasModal } from './PessoasModal';

const ACTIONS: Array<{ value: ReviewAction; label: string }> = [
  { value: 'validate', label: 'Validar' },
  { value: 'validate_with_note', label: 'Validar com ressalva' },
  { value: 'request_correction', label: 'Solicitar correção' },
  { value: 'reject', label: 'Rejeitar' },
];

interface Props {
  document: CollaboratorDocument | null;
  onClose: () => void;
  onDone: (message: string) => void;
}

/** Resultado da validação (RN-GP-002-030/031). Rejeição e correção avisam o colaborador. */
export function ReviewDocumentModal({ document, onClose, onDone }: Props) {
  const analisar = useAnalisarDocumento();
  const [action, setAction] = useState<ReviewAction>('validate');
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setAction('validate');
    setReason('');
    setComment('');
    setError(null);
  }, [document]);

  async function submit() {
    if (!document) return;
    if (action === 'reject' && !reason) return setError('Selecione o motivo da rejeição.');
    if (action !== 'validate' && !comment.trim()) {
      return setError(action === 'validate_with_note' ? 'Descreva a ressalva.' : 'Escreva o que o colaborador deve fazer.');
    }
    try {
      await analisar.mutateAsync({ id: document.id, action, reason: reason || undefined, comment: comment.trim() || undefined });
      onDone(ACTIONS.find((a) => a.value === action)?.label === 'Validar' ? 'Documento validado.' : 'Análise registrada.');
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível registrar a análise.'));
    }
  }

  return (
    <PessoasModal
      open={document !== null}
      title={`Analisar — ${document?.typeName ?? ''}`}
      description={`Versão ${document?.version ?? ''} · confira legibilidade, completude, validade e dados do cadastro.`}
      submitLabel="Registrar"
      submitVariant={action === 'reject' ? 'danger' : 'primary'}
      isSubmitting={analisar.isPending}
      submitError={error}
      onClose={onClose}
      onSubmit={submit}
    >
      <AutocompleteSelect label="Resultado" options={ACTIONS} value={action} onChange={(v) => setAction(v as ReviewAction)} />
      {action === 'reject' && (
        <AutocompleteSelect label="Motivo" options={REJECTION_REASON_OPTIONS} value={reason} onChange={setReason} />
      )}
      {action !== 'validate' && (
        <Textarea
          label={action === 'validate_with_note' ? 'Ressalva' : 'Comentário para o colaborador'}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      )}
    </PessoasModal>
  );
}
